import { useCallback } from 'react';
import { Trash2, Scissors } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ContextMenuItem } from '../../components/ContextMenu';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import type { CanvasSegment } from '../types';
import { GRID_SIZE, screenToCanvas } from '../types';
import { buildStyleSubmenus, buildLabelItems } from '../lib/contextMenuHelpers';
import { useCanvasStores, useCanvasRotation, materializeSegmentsFromStore } from '../stores/CanvasContext';
import { globalTasksStore } from '../../stores/globalTasksStore';
import type { ContextMenuSetter } from './useCanvasContextMenus';
import type { CanvasStore } from '../stores/canvasStore';

function buildDeleteItems(
  segmentId: string,
  segments: CanvasSegment[],
  store: CanvasStore,
  t: (key: any, options?: Record<string, unknown>) => string,
  txManager?: import('../commands/transactionManager').TransactionManager,
): ContextMenuItem[] {
  const items: ContextMenuItem[] = [
    {
      id: 'delete-segment',
      label: t('canvasMenu.deleteConnection'),
      icon: Trash2,
      iconColor: '#dc2626',
      color: '#dc2626',
      hoverBg: '#fef2f2',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: segmentId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    },
  ];

  const segmentIds = new Set(segments.map(s => s.id));
  const selectedSegmentIds = canvasSelectionStore.getSnapshot().context.selectedEntityIds.filter(id => segmentIds.has(id));
  if (selectedSegmentIds.length > 1 && selectedSegmentIds.includes(segmentId)) {
    items.push({
      id: 'delete-selected-segments',
      label: t('canvasMenu.deleteConnections', { count: selectedSegmentIds.length }),
      icon: Trash2,
      iconColor: '#dc2626',
      color: '#dc2626',
      hoverBg: '#fef2f2',
      onClick: () => {
        txManager?.begin('delete-segments');
        for (const eid of selectedSegmentIds) {
          store.send({ type: 'entity.remove', entityId: eid });
        }
        txManager?.commit();
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });
  }

  return items;
}

export function useEdgeContextMenu(setContextMenu: ContextMenuSetter) {
  const { store, containerRef, panZoomStore, txManager } = useCanvasStores();
  const rotation = useCanvasRotation();
  const { t } = useTranslation();

  const handleSegmentSplit = useCallback((segmentId: string, screenX: number, screenY: number) => {
    if (!containerRef.current) return;
    const pz = panZoomStore.getSnapshot().context;
    const canvasPoint = screenToCanvas(screenX, screenY, containerRef.current, pz, rotation);
    const snappedX = Math.round(canvasPoint.x / GRID_SIZE) * GRID_SIZE;
    const snappedY = Math.round(canvasPoint.y / GRID_SIZE) * GRID_SIZE;
    const waypointId = `wp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    store.send({
      type: 'connector.split',
      connectorId: segmentId,
      waypointId,
      x: snappedX,
      y: snappedY,
    });
  }, [store, panZoomStore, rotation, containerRef]);

  const handleSegmentContextMenu = useCallback((segmentId: string, e: React.MouseEvent) => {

    const segments = materializeSegmentsFromStore(store, globalTasksStore.getSnapshot().context.tasksById);
    const segment = segments.find(s => s.id === segmentId);
    if (!segment) return;

    const updateStyle = (changes: Record<string, unknown>) => {
      store.send({ type: 'connector.updateStyle', connectorId: segmentId, changes });
    };

    const items: ContextMenuItem[] = [
      {
        id: 'split',
        label: t('canvasMenu.split'),
        icon: Scissors,
        onClick: () => handleSegmentSplit(segmentId, e.clientX, e.clientY),
      },
      ...buildStyleSubmenus(segment, segmentId, store, updateStyle),
      ...buildLabelItems(segment.label, updateStyle),
      ...buildDeleteItems(segmentId, segments, store, t, txManager),
    ];

    setContextMenu({ x: e.clientX, y: e.clientY, items });
  }, [store, handleSegmentSplit, setContextMenu]);

  return { handleSegmentContextMenu };
}
